import { relTime } from '../lib/useFreshness';

/**
 * "Last scanned" line for a detail page — contract point 5 of the redesign: a
 * detail page that silently shows week-old truth is the original problem in
 * miniature. Every scanned entity says when its row was last confirmed.
 *
 * Amber past `staleAfterH` (default 24h) because the number alone doesn't tell a
 * reader whether 3 days is normal for this entity.
 *
 * Pass `curated` for entities that are NOT scanned — with `lead` saying WHY, since
 * the reason differs: the deploy catalog is a hand-written seed, while a scheduler
 * job's freshness is simply its last run. Claiming a scan time for either would be
 * a lie, and showing nothing would leave the reader guessing.
 *
 * `lead` ALSO replaces the verb in the timestamped case, because not everything
 * with an age was scanned: the agent fleet's is its newest heartbeat and Atlas's
 * is its last reconcile. Telling an operator their fleet "was last scanned 10d
 * ago" names a scan that does not exist.
 */
export function ScannedAt({
  at,
  staleAfterH = 24,
  curated = false,
  lead,
  note,
}: {
  at?: string | null;
  staleAfterH?: number;
  curated?: boolean;
  /** When `curated`, the sentence saying why this entity has no timestamp at all.
   *  Otherwise the verb phrase before the age — "Last reconciled", "Last heard
   *  from". Defaults to the scanner wording. */
  lead?: string;
  note?: string;
}) {
  if (curated) {
    return (
      <p className="scanned-at">
        {lead ?? 'Curated content — maintained by hand, not scanned.'}
        {note ? ` ${note}` : ''}
      </p>
    );
  }
  if (!at) return <p className="scanned-at scanned-at--warn">{lead ?? 'Never scanned'}.</p>;
  const ageMs = Date.now() - new Date(at).getTime();
  const stale = ageMs > staleAfterH * 3_600_000;
  return (
    <p className={`scanned-at${stale ? ' scanned-at--warn' : ''}`}>
      {lead ?? 'Last scanned'} {relTime(ageMs)} ago
      <span className="muted"> · {new Date(at).toLocaleString()}</span>
      {stale && <span className="muted"> — older than usual for this page</span>}
      {note ? <span className="muted"> · {note}</span> : null}
    </p>
  );
}
